import React, { useEffect, useRef, useState } from 'react';

/**
 * ATLAS SAHNE — crm.whowsell.com tanıtım sayfasındaki 3B robotun bu projeye
 * taşınmış hâli.
 *
 * Kaynak: crm-whowsell/src/components/landing/SplineStage.tsx
 * Bu dosya o bileşenin mantığını BİREBİR taşır; yalnızca üç şey değişti ve
 * her biri kendi yerinde gerekçesiyle yazıldı:
 *
 *   1. TypeScript tipleri kaldırıldı (bu proje saf JavaScript).
 *   2. Sahne artık GÖRÜNÜR ALANA GİRİNCE yükleniyor (aşağıda "TEMBEL KAPI").
 *      Kaynakta sahne sayfanın kahramanıydı ve ilk ekranda duruyordu; burada
 *      sayfanın ortasında küçük bir düğüm. 13,5 MB'ı açılışta indirmek
 *      kabul edilemez.
 *   3. Sahne çizilemediğinde (WebGL yok, ağ koptu, wasm patladı) veya
 *      kullanıcı hareket azaltma istediğinde kutu BOŞ KALMIYOR: çağıranın
 *      verdiği `yedek` düğümü (statik ikon) yerinde duruyor.
 *
 * Bakış takibi matematiği, sahiplik nöbeti, çizim bütçesi ve durdurma/başlatma
 * kuralları kaynaktan hiç sapmadan kopyalandı.
 */

const SAHNE_URL = '/vendor/w3d/scene.bin';
const GORUNTULEYICI_SRC = '/vendor/w3d/w3d-viewer.js';
const GORUNTULEYICI_ID = 'wh-atlas-viewer-loader';
const ONYUKLEME_ID = 'wh-atlas-onyukleme';

const SHADOW_CSS =
  ':host,#container,#canvas3d,canvas{background:transparent!important;background-color:transparent!important}' +
  '#logo,#spline-watermark,a[href*="spline.design"],video{display:none!important}';

let viewerPromise = null;
let sharedViewer = null;
let sharedReady = false;
let introPlayed = false;

/**
 * SAHİPLİK NÖBETİ — tek görüntüleyici, tek sahip.
 *
 * `sharedViewer` modül düzeyinde TEK bir düğüm. Aynı anda iki `AtlasSahne`
 * çizilirse ikinci `appendChild` düğümü birincinin içinden söker; birinci
 * bileşen sonradan söküldüğünde de kendi olmayan bir düğümü DOM'dan atar ve
 * ikisi de boş kalır.
 *
 * Buradaki nöbet emniyet katmanı: bir bileşen sökülürken düğümü YALNIZCA
 * hâlâ sahibiyse alıyor. React aynı commit içinde önce tüm sökme işlerini,
 * sonra tüm bağlama işlerini çalıştırdığı için sıra doğal olarak "eski
 * bırakır, yeni alır" biçiminde ilerler; nöbet o sıranın bozulduğu durumları
 * da kapatır.
 */
let stageSeq = 0;
let stageOwner = 0;

/**
 * BAKIŞ KİLİDİ — sahne, taşınırken imleci İZLEMESİN.
 *
 * Kaynakta sahne sürüklenebilir bir düğmenin içinde de çiziliyordu. Sürükleme
 * sırasında iki şey aynı anda değişiyordu: imleç hareket ediyordu (robot
 * kafasını çeviriyordu) ve tuvalin KENDİSİ hareket ediyordu (ama `rect`
 * yalnızca kaydırma/boyutlanma anlarında ölçülüyordu). İkisi birleşince kafa
 * önce yanlış yere dönüp sonra geri sıçrıyordu.
 *
 * Kilit açık olduğu sürece hiçbir yapay `pointermove` üretilmiyor ve çizim
 * döngüsü boşta duruyor. Kilit kalkınca ölçüm önbellekleri sıfırlanıp
 * yeniden ölçülüyor.
 *
 * Bu projede şu an sürüklenen bir kap yok; API dışarı açık bırakıldı ki
 * ileride sahne taşınabilir bir kutuya konursa aynı koruma elde hazır olsun.
 */
let stagePointerLocked = false;
const stageLockListeners = new Set();

export function atlasBakisKilidi(kilitli) {
  if (stagePointerLocked === kilitli) return;
  stagePointerLocked = kilitli;
  for (const dinleyici of stageLockListeners) dinleyici(kilitli);
}

/* ÖN YÜKLEME ETİKETLERİ — tembel kapı açıldığı AN eklenir.
   Kaynakta bunlar JSX içindeydi ve bileşen çizilir çizilmez head'e taşınıyordu;
   burada sahne çok sonra yükleneceği için o an eklemek indirmeyi erkene
   çekmek yerine sadece boşa bant harcardı. Kapı açılınca eklemek ise gerçek
   kazanç: görüntüleyici 2,2 MB'lık dosyayı çözümlemeden önce tarayıcı
   scene.bin ve process.wasm indirmelerine paralel başlayabiliyor. */
function onyuklemeEkle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(ONYUKLEME_ID)) return;
  const kap = document.createDocumentFragment();
  const yap = (rel, href, as) => {
    const el = document.createElement('link');
    el.rel = rel;
    el.href = href;
    if (as) {
      el.as = as;
      el.crossOrigin = 'anonymous';
    }
    return el;
  };
  const ilk = yap('modulepreload', GORUNTULEYICI_SRC);
  ilk.id = ONYUKLEME_ID;
  kap.appendChild(ilk);
  kap.appendChild(yap('preload', SAHNE_URL, 'fetch'));
  kap.appendChild(yap('preload', '/vendor/w3d/process.wasm', 'fetch'));
  document.head.appendChild(kap);
}

function loadViewer() {
  if (viewerPromise) return viewerPromise;
  viewerPromise = new Promise((resolve, reject) => {
    if (customElements.get('spline-viewer')) {
      resolve();
      return;
    }
    const existing = document.getElementById(GORUNTULEYICI_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('viewer')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = GORUNTULEYICI_ID;
    script.type = 'module';
    script.src = GORUNTULEYICI_SRC;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('viewer')), { once: true });
    document.head.appendChild(script);
  });
  return viewerPromise;
}

function paintShadow(host) {
  const root = host.shadowRoot;
  if (!root) return false;
  if (root.getElementById('wh-atlas-clean')) return true;
  const style = document.createElement('style');
  style.id = 'wh-atlas-clean';
  style.textContent = SHADOW_CSS;
  root.appendChild(style);
  return true;
}

const TAA_BUDGET = 3;
const TAA_STOP = 31;

const perf = { cost: 8, gap: 40, ratio: 0, last: 0, shadow: 0 };

const MIN_FPS_GAP = 16;
const MAX_FPS_GAP = 120;

function frameGap() {
  const gap = perf.cost * 2.4;
  return gap < MIN_FPS_GAP ? MIN_FPS_GAP : gap > MAX_FPS_GAP ? MAX_FPS_GAP : gap;
}

function getApp(host) {
  const app = host._spline || host.application || host._app;
  return app || null;
}

/**
 * SAHNEYİ GERÇEKTEN DURDURMAK — ve `_isPaused`'ın neden işe yaramadığı.
 *
 * Görüntüleyicinin kaynağı okunarak doğrulandı
 * (public/vendor/w3d/w3d-viewer.js · Application sınıfı):
 *
 *   stop() { this._renderer?.setAnimationLoop(null); this._isPaused = true; ... }
 *   play() { if (!this._isPaused) return
 *            this._isPaused = false
 *            this._renderer?.setAnimationLoop(this.render) }
 *
 * Çizim döngüsünü söken ya da takan TEK ŞEY `setAnimationLoop`. `_isPaused`
 * onun bıraktığı izden ibaret, bir anahtar değil. Bayrağı elle yazmak iki
 * yönde de zarar verir: `true` yazmak döngüyü durdurmaz ama "boş kareyi atla"
 * iyileştirmesini devre dışı bırakır; `false` yazmak da gerçekten durmuş bir
 * sahnede play()'i erken döndürüp sahneyi kalıcı olarak dondurur.
 *
 * Bu yüzden her iki yön de resmî API üzerinden yapılıyor.
 */
function stopScene(host) {
  const app = host ? getApp(host) : null;
  if (app && typeof app.stop === 'function') app.stop();
}

function playScene(host) {
  const app = host ? getApp(host) : null;
  if (app && typeof app.play === 'function') app.play();
}

function budgetRatio() {
  const dpr = window.devicePixelRatio || 1;
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const cores = navigator.hardwareConcurrency || 4;
  if (coarse || cores <= 4) return 1;
  if (cores <= 8) return Math.min(dpr, 1.15);
  return Math.min(dpr, 1.25);
}

function capTaa(app) {
  if (app.__whAtlasTuned || typeof app.render !== 'function') return;
  const original = app.render.bind(app);
  const shadowMap = app._renderer && app._renderer.shadowMap;
  if (shadowMap) shadowMap.autoUpdate = false;

  app.render = (time) => {
    const started = performance.now();
    if (started - perf.last < perf.gap) return;
    perf.last = started;
    if (shadowMap) {
      perf.shadow += 1;
      if (perf.shadow >= 6) {
        perf.shadow = 0;
        shadowMap.needsUpdate = true;
      }
    }
    original(time);
    const spent = performance.now() - started;
    if (spent > 0.2) perf.cost = perf.cost * 0.8 + spent * 0.2;
    perf.gap = frameGap();
    if ((app.currentTAAFrame ?? 0) > TAA_BUDGET) app.currentTAAFrame = TAA_STOP;
    if (perf.cost > 14 && perf.ratio > 0.8) {
      perf.ratio = Math.max(0.75, perf.ratio - 0.12);
      if (app._renderer && typeof app._renderer.setPixelRatio === 'function') {
        app._renderer.setPixelRatio(perf.ratio);
      }
    }
  };
  app.__whAtlasTuned = true;
  window.__whAtlasPerf = perf;
}

function refit(host) {
  const app = getApp(host);
  if (!app) return;
  const renderer = app._renderer;
  perf.last = 0;
  if (typeof app._resize === 'function') app._resize();
  else if (typeof app._debouncedResize === 'function') app._debouncedResize();
  if (renderer && typeof renderer.setPixelRatio === 'function' && perf.ratio > 0) {
    renderer.setPixelRatio(perf.ratio);
  }
  const ctx =
    (app.eventManager && app.eventManager.eventContext) ||
    (app._eventManager && app._eventManager.eventContext);
  const canvas = host.shadowRoot && host.shadowRoot.querySelector('canvas');
  if (ctx && canvas) ctx.domRect = canvas.getBoundingClientRect();
  if (typeof app.requestRender === 'function') app.requestRender();
}

function applyBudget(host) {
  const app = getApp(host);
  const renderer = app && app._renderer;
  if (!app || !renderer || typeof renderer.setPixelRatio !== 'function') return false;
  capTaa(app);
  const wanted = budgetRatio();
  const now = typeof renderer.getPixelRatio === 'function' ? renderer.getPixelRatio() : 0;
  if (Math.abs(now - wanted) < 0.01) return true;
  perf.ratio = wanted;
  renderer.setPixelRatio(wanted);
  if (typeof app.requestRender === 'function') app.requestRender();
  return true;
}

function getViewer() {
  if (sharedViewer) return sharedViewer;
  const viewer = document.createElement('spline-viewer');
  viewer.setAttribute('url', SAHNE_URL);
  viewer.setAttribute('loading-anim-type', 'none');
  viewer.setAttribute('loading', 'eager');
  viewer.setAttribute('background', 'transparent');
  viewer.style.width = '100%';
  viewer.style.height = '100%';
  viewer.style.display = 'block';
  viewer.addEventListener('load-complete', () => {
    sharedReady = true;
    paintShadow(viewer);
    applyBudget(viewer);
    /* Sahne, kendisini isteyen bileşen SÖKÜLDÜKTEN SONRA yüklenmiş olabilir:
       kullanıcı bölümü hızlıca geçtiyse görüntüleyici DOM'dan çıkarılır ama
       indirme sürer ve bittiğinde çizim döngüsü başlar. Görüntüleyici o an
       belgeye bağlı değilse döngü daha ilk karede duruyor; sökme dalındaki
       `stop()` o anda henüz var olmayan bir uygulamada çalışamadığı için bu
       ikinci kapı gerekli. */
    if (!viewer.isConnected) stopScene(viewer);
    document.documentElement.setAttribute('data-robot', 'ready');
  });
  sharedViewer = viewer;
  return viewer;
}

/**
 * SAHNE HİÇ BAŞLAMASIN DENETİMİ.
 *
 * İki durumda tek bir bayt bile indirilmiyor:
 *   · `prefers-reduced-motion: reduce` — kullanıcı hareket istemiyor.
 *   · WebGL yok — bağlam açılamıyorsa sahne zaten çizilemez; 13,5 MB'ı
 *     indirip sonra boş kutu göstermek kullanıcıya iki kere zarar verir.
 *
 * Denetim bağlamı açıp hemen kapatıyor: `loseContext` varsa çağrılıyor, yoksa
 * tuval çöpe gidince bağlam da gidiyor.
 */
function sahneCizilebilir() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  try {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return false;
    }
  } catch (e) {
    /* matchMedia yoksa hareket tercihi bilinemez; sahneyi engellemiyoruz. */
  }
  try {
    const tuval = document.createElement('canvas');
    const gl = tuval.getContext('webgl2') || tuval.getContext('webgl');
    if (!gl) return false;
    const kayip = gl.getExtension('WEBGL_lose_context');
    if (kayip) kayip.loseContext();
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * `onHazir` — sahnenin GERÇEKTEN çizilip çizilmediğini dışarı bildirir.
 * `onKirildi` — yükleme KIRILDI haberi; `onHazir(false)`den ayrı olmak
 * zorunda, çünkü o haber "henüz hazır değil" ile "bir daha hazır olmayacak"
 * durumlarını aynı değerle anlatıyor.
 *
 * `yedek` — sahne yokken kutuda duracak düğüm (statik ikon). Kaynakta bu
 * karar çağırana bırakılmıştı; burada da öyle, ama düğüm bu bileşenin içinde
 * çiziliyor ki kutu hiçbir karede boş kalmasın.
 */
export default function AtlasSahne({ yedek = null, etiket = '', onHazir, onKirildi }) {
  const hostRef = useRef(null);
  const mountRef = useRef(null);
  const [hazir, setHazir] = useState(false);
  const [yedekGoster, setYedekGoster] = useState(true);

  /* Geri çağrı REF'te tutuluyor: doğrudan bağımlılık listesine yazılsaydı,
     çağıran her yeniden çizimde yeni bir işlev üretti mi sahne sökülüp
     yeniden bağlanırdı. */
  const onHazirRef = useRef(onHazir);
  const onKirildiRef = useRef(onKirildi);
  useEffect(() => {
    onHazirRef.current = onHazir;
    onKirildiRef.current = onKirildi;
  });

  useEffect(() => {
    if (!mountRef.current) return undefined;

    /* Sahne hiç başlamayacaksa buradan çıkılıyor: ne script, ne scene.bin,
       ne wasm. Yedek düğüm kutuda kalıyor. */
    if (!sahneCizilebilir()) {
      onHazirRef.current?.(false);
      onKirildiRef.current?.();
      return undefined;
    }

    stageSeq += 1;
    const benimSira = stageSeq;
    stageOwner = benimSira;

    let cancelled = false;
    let paintTimer = 0;
    let sizeObserver = null;
    let kapiGozcusu = null;
    let lastW = 0;
    let lastH = 0;

    const attach = () => {
      if (cancelled || !mountRef.current) return;
      const viewer = getViewer();
      if (viewer.parentElement !== mountRef.current) mountRef.current.appendChild(viewer);
      /* Yeniden bağlanışta çizim döngüsünü geri tak. Sahne modül düzeyinde
         TEK örnek; bir önceki bileşen sökülürken gerçekten durdurulmuştu. */
      playScene(viewer);

      /* Tek kapı: durum hem içeride hem dışarıda AYNI anda değişsin.
         İki ayrı yerde setHazir(true) yazıp geri çağrıyı birinde unutmak,
         yedeğin sahnenin altında takılı kalmasının tam sebebiydi. */
      const hazirIsaretle = () => {
        if (cancelled) return;
        setHazir(true);
        onHazirRef.current?.(true);
      };

      if (sharedReady) {
        hazirIsaretle();
        document.documentElement.setAttribute('data-robot', 'ready');
      } else {
        viewer.addEventListener('load-complete', hazirIsaretle, { once: true });
      }

      const paintTick = () => {
        if (cancelled) return;
        const done = paintShadow(viewer) && applyBudget(viewer);
        paintTimer = window.setTimeout(paintTick, done ? 1500 : 16);
      };
      paintTick();

      Promise.resolve(viewer.updateComplete)
        .then(() => {
          if (cancelled) return;
          paintShadow(viewer);
          viewer._isElementInViewport = true;
          if (!viewer._loaded && typeof viewer.load === 'function') viewer.load();
        })
        .catch(() => undefined);

      const syncSize = () => {
        const node = mountRef.current;
        if (!node) return;
        const width = node.clientWidth;
        const height = node.clientHeight;
        if (!width || !height) return;
        if (width === lastW && height === lastH) return;
        lastW = width;
        lastH = height;
        window.dispatchEvent(new Event('resize'));
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (!cancelled) refit(viewer);
          });
        });
      };

      if (typeof ResizeObserver !== 'undefined') {
        sizeObserver = new ResizeObserver(syncSize);
        sizeObserver.observe(mountRef.current);
      }
      syncSize();
    };

    const kirildi = () => {
      if (cancelled) return;
      /* Yükleme kırıldı: yedek işaret DOM'da kalsın, kutu boş kalmasın.
         `onKirildi` ayrı bir haber, çünkü onHazir(false) "henüz değil" ile
         "hiç olmayacak"ı ayırt etmiyor. */
      onHazirRef.current?.(false);
      onKirildiRef.current?.();
    };

    const waitForSize = () => {
      if (cancelled || !mountRef.current) return;
      if (mountRef.current.clientWidth > 0 && mountRef.current.clientHeight > 0) {
        onyuklemeEkle();
        loadViewer().then(attach).catch(kirildi);
        return;
      }
      window.requestAnimationFrame(waitForSize);
    };

    /**
     * TEMBEL KAPI — kaynaktan ayrıldığımız tek yapısal nokta.
     *
     * Varlıkların toplamı 13,5 MB (viewer 2,2 MB + scene.bin 1,3 MB + wasm'lar).
     * Kaynakta sahne sayfanın ilk ekranıydı, burada sayfanın ortasında küçük
     * bir düğüm: kullanıcı oraya hiç inmeyebilir. Bu yüzden indirme, kutu
     * görüş alanına 200px yaklaşana kadar başlamıyor.
     *
     * `IntersectionObserver` yoksa (çok eski tarayıcı) doğrudan yükleniyor —
     * eksik özellik yüzünden sahneyi hiç göstermemek yanlış olurdu.
     */
    const kapiAc = () => {
      if (kapiGozcusu) {
        kapiGozcusu.disconnect();
        kapiGozcusu = null;
      }
      waitForSize();
    };

    if (typeof IntersectionObserver === 'undefined') {
      kapiAc();
    } else {
      kapiGozcusu = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              kapiAc();
              return;
            }
          }
        },
        { rootMargin: '200px' },
      );
      kapiGozcusu.observe(mountRef.current);
    }

    return () => {
      cancelled = true;
      if (paintTimer) window.clearTimeout(paintTimer);
      if (sizeObserver) sizeObserver.disconnect();
      if (kapiGozcusu) kapiGozcusu.disconnect();
      /* Sahne bu kutudan gidiyor: yedek geri gelsin, kutu boş kalmasın. */
      onHazirRef.current?.(false);
      /* NÖBETİ BIRAKMADAN SÖKME. Bir başka örnek görüntüleyiciyi çoktan
         devraldıysa (nöbet numarası değişmişse) burada durdurup DOM'dan
         almak, o örneğin ayaklarının altındaki düğümü çekmek olurdu. */
      if (stageOwner !== benimSira) return;
      /* ÖNCE DURDUR, sonra DOM'dan al. DOM'dan çıkarmak WebGL çizim
         döngüsünü durdurmuyor: döngü `setAnimationLoop` ile renderer'a
         takılı, öğenin belgede olup olmamasıyla ilgisi yok. */
      stopScene(sharedViewer);
      if (sharedViewer && sharedViewer.parentNode) {
        sharedViewer.parentNode.removeChild(sharedViewer);
      }
    };
  }, []);

  /* Yedeği DOM'DAN ÇIKARMA — saydamlaştırmak yetmez.
     Sahne yüklendikten sonra yedek ikon DOM'da kalırsa WebGL tuvalinin
     saydam pikselleri altındakini geçirir ve iki robot izlenimi doğar.
     Önce 350ms'lik solma sınıfı, sonra gerçek sökme. */
  useEffect(() => {
    if (!hazir) {
      setYedekGoster(true);
      return undefined;
    }
    const t = window.setTimeout(() => setYedekGoster(false), 380);
    return () => window.clearTimeout(t);
  }, [hazir]);

  useEffect(() => {
    const node = hostRef.current;
    if (!node) return undefined;

    const wrap = node.closest('.wh-atlas-sargi');
    let introTimer = 0;
    if (wrap) {
      if (introPlayed) {
        wrap.classList.remove('intro-run');
        wrap.classList.add('intro-done');
      } else {
        wrap.classList.add('intro-run');
        introTimer = window.setTimeout(() => {
          introPlayed = true;
          wrap.classList.remove('intro-run');
          wrap.classList.add('intro-done');
        }, 1250);
      }
    }

    const REACH = 1.55;
    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    let seeded = false;
    let frame = 0;
    let active = false;
    let lastEmit = 0;
    let canvas = null;
    let eventContext = null;
    let rect = null;
    let inView = true;
    let pageLive = true;
    const isLive = () => inView && pageLive;

    const findCanvas = () => {
      if (canvas && canvas.isConnected) return canvas;
      const viewer = node.querySelector('spline-viewer');
      if (!viewer || !viewer.shadowRoot) return null;
      canvas = viewer.shadowRoot.querySelector('canvas');
      return canvas;
    };

    const findContext = () => {
      if (eventContext) return eventContext;
      const viewer = node.querySelector('spline-viewer');
      if (!viewer) return null;
      const app = getApp(viewer);
      const ctx =
        (app && app.eventManager && app.eventManager.eventContext) ||
        (app && app._eventManager && app._eventManager.eventContext);
      if (ctx && 'domRect' in ctx) eventContext = ctx;
      return eventContext;
    };

    const measure = () => {
      const surface = findCanvas();
      if (!surface) {
        rect = null;
        return;
      }
      const next = surface.getBoundingClientRect();
      if (next.width < 2 || next.height < 2) {
        rect = null;
        return;
      }
      rect = next;
      const ctx = findContext();
      if (ctx) ctx.domRect = next;
      if (!seeded) {
        seeded = true;
        current.x = next.left + next.width / 2;
        current.y = next.top + next.height * 0.4;
        target.x = current.x;
        target.y = current.y;
      }
    };

    const emit = () => {
      const surface = findCanvas();
      if (!surface) return;
      surface.dispatchEvent(
        new PointerEvent('pointermove', {
          clientX: current.x,
          clientY: current.y,
          bubbles: true,
          cancelable: true,
          composed: true,
          pointerId: 1,
          pointerType: 'mouse',
          isPrimary: true,
          view: window,
        }),
      );
    };

    const loop = () => {
      current.x += (target.x - current.x) * 0.2;
      current.y += (target.y - current.y) * 0.2;
      const now = performance.now();
      const settled =
        Math.abs(target.x - current.x) < 0.4 && Math.abs(target.y - current.y) < 0.4;
      if (settled) {
        current.x = target.x;
        current.y = target.y;
        emit();
        frame = 0;
        active = false;
        return;
      }
      if (now - lastEmit >= perf.gap) {
        lastEmit = now;
        emit();
      }
      frame = requestAnimationFrame(loop);
    };

    const start = () => {
      /* Bakış kilidi: kap taşınırken tek bir yapay imleç olayı bile
         üretilmiyor. Zamanlayıcılar ve gözlemciler de buradan geçtiği için
         tek kapı yetiyor. */
      if (active || stagePointerLocked || !isLive()) return;
      if (!rect) measure();
      if (!rect) return;
      active = true;
      frame = requestAnimationFrame(loop);
    };

    const track = (clientX, clientY) => {
      if (stagePointerLocked) return;
      if (!rect) measure();
      if (!rect) return;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const halfW = rect.width * REACH;
      const halfH = rect.height * REACH;
      const minX = cx - halfW;
      const maxX = cx + halfW;
      const minY = cy - halfH;
      const maxY = cy + halfH;
      target.x = clientX < minX ? minX : clientX > maxX ? maxX : clientX;
      target.y = clientY < minY ? minY : clientY > maxY ? maxY : clientY;
      start();
    };

    const onPointerMove = (event) => track(event.clientX, event.clientY);
    const onTouch = (event) => {
      const touch = event.touches[0] || event.changedTouches[0];
      if (touch) track(touch.clientX, touch.clientY);
    };
    const onViewportChange = () => {
      measure();
      start();
    };

    const settleTimers = [120, 400, 900, 1800, 3200].map((delay) =>
      window.setTimeout(() => {
        measure();
        start();
      }, delay),
    );

    let visObserver = null;
    if (typeof IntersectionObserver !== 'undefined') {
      visObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) inView = entry.isIntersecting;
          const viewer = node.querySelector('spline-viewer');
          if (isLive()) {
            playScene(viewer);
            measure();
            start();
          } else {
            stopScene(viewer);
            if (frame) {
              cancelAnimationFrame(frame);
              frame = 0;
              active = false;
            }
          }
        },
        { rootMargin: '80px' },
      );
      visObserver.observe(node);
    }

    const onVisibility = () => {
      pageLive = document.visibilityState === 'visible';
      const viewer = node.querySelector('spline-viewer');
      if (isLive()) {
        playScene(viewer);
        canvas = null;
        eventContext = null;
        rect = null;
        perf.last = 0;
        measure();
        start();
      } else {
        stopScene(viewer);
        if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
          active = false;
        }
      }
    };

    const onFocus = () => {
      pageLive = true;
      /* Geri dönüşte çizim döngüsünü de geri tak. `visibilitychange` her
         zaman gelmiyor (pencere değişimi, geri/ileri önbelleğinden dönüş) ve
         döngü sökülü kalırsa sahne donmuş görünür. */
      if (inView) playScene(node.querySelector('spline-viewer'));
      canvas = null;
      eventContext = null;
      rect = null;
      perf.last = 0;
      measure();
      start();
    };

    /**
     * Kilit değişimi.
     *
     * KİLİTLENİRKEN: süren kare isteği iptal ediliyor. Yoksa taşıma
     * başladıktan sonra bile yolda kalan bir kare daha yapay `pointermove`
     * gönderir ve kafa son bir kez sıçrardı.
     *
     * AÇILIRKEN: tuval artık BAŞKA bir yerde. Ölçüm önbelleklerinin hepsi
     * (tuval düğümü, olay bağlamı, dikdörtgen) sıfırlanıp yeniden okunuyor.
     */
    const kilitDegisti = (kilitli) => {
      if (kilitli) {
        if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
          active = false;
        }
        return;
      }
      canvas = null;
      eventContext = null;
      rect = null;
      perf.last = 0;
      measure();
      start();
    };
    stageLockListeners.add(kilitDegisti);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onFocus);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerdown', onPointerMove, { passive: true });
    window.addEventListener('touchstart', onTouch, { passive: true });
    window.addEventListener('touchmove', onTouch, { passive: true });
    window.addEventListener('scroll', onViewportChange, { passive: true });
    window.addEventListener('resize', onViewportChange, { passive: true });

    return () => {
      if (frame) cancelAnimationFrame(frame);
      if (introTimer) window.clearTimeout(introTimer);
      for (const id of settleTimers) window.clearTimeout(id);
      stageLockListeners.delete(kilitDegisti);
      if (visObserver) visObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onFocus);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerMove);
      window.removeEventListener('touchstart', onTouch);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('scroll', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
    };
  }, []);

  return (
    <div className="wh-atlas-sargi">
      <div ref={hostRef} className={hazir ? 'wh-atlas hazir' : 'wh-atlas'} data-testid="atlas-sahne">
        <div ref={mountRef} className="wh-atlas-tuval" />

        {yedekGoster && yedek ? (
          <div className="wh-atlas-yedek" aria-hidden="true" data-testid="atlas-yedek">
            {yedek}
          </div>
        ) : null}

        {etiket ? <span className="wh-atlas-etiket">{etiket}</span> : null}
      </div>
    </div>
  );
}
